import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Flex,
  Text,
  Input,
  Button,
  HStack,
  VStack,
  Badge,
  Spinner,
  Tabs,
  TabList,
  Tab,
  useDisclosure,
  InputGroup,
  InputLeftElement,
  Tooltip,
} from '@chakra-ui/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  MagnifyingGlass,
  Plus,
  CalendarBlank,
  Ticket,
  MapPin,
  ChartLineUp,
  ChartBar,
  GearSix,
} from 'phosphor-react';
import { toast } from 'sonner';
import moment from 'moment';
import 'moment/locale/pt-br';
import { Link as RouterLink, Navigate, useSearchParams, useNavigate } from 'react-router-dom';
import { PageBox } from '../../components/pageBox';
import { useAuth } from '../../providers/auth';
import { useEventos, IEventoListItem, IEventoAlerta } from '../../hooks/useEventos';
import { IDataNoPagination } from '../../types/shared';
import { formatCurrency } from '../../utils/FormatFunctions';
import EventoForm from './components/EventoForm';
import FinanceiroDashboard from './components/FinanceiroDashboard';
import PainelExecutivo from './components/PainelExecutivo';
import AlertasDropdown from './components/AlertasDropdown';
import EventoConfigModal from './components/EventoConfigModal';

const STATUS_BADGE: Record<string, { color: string; label: string; gradient: string }> = {
  proposta: { color: 'yellow', label: 'Proposta', gradient: 'linear(to-r, #EAB308, #F97316)' },
  contrato: { color: 'blue', label: 'Contrato', gradient: 'linear(to-r, #3B82F6, #6366F1)' },
  enviado_assinatura: {
    color: 'orange',
    label: 'Enviado p/ Ass.',
    gradient: 'linear(to-r, #F97316, #EAB308)',
  },
  assinado: { color: 'cyan', label: 'Assinado', gradient: 'linear(to-r, #06B6D4, #3B82F6)' },
  aguardando_nf: {
    color: 'teal',
    label: 'Aguardando NF',
    gradient: 'linear(to-r, #14B8A6, #06B6D4)',
  },
  nf_emitida: { color: 'purple', label: 'NF Emitida', gradient: 'linear(to-r, #A855F7, #6366F1)' },
  pago: { color: 'green', label: 'Pago', gradient: 'linear(to-r, #22C55E, #14B8A6)' },
};

const CARD_GRADIENTS = [
  'linear(to-r, #6366F1, #8B5CF6)',
  'linear(to-r, #EC4899, #F43F5E)',
  'linear(to-r, #F97316, #EAB308)',
  'linear(to-r, #22C55E, #14B8A6)',
  'linear(to-r, #06B6D4, #3B82F6)',
  'linear(to-r, #A855F7, #6366F1)',
];

const VIEW_MODES = ['eventos', 'painel', 'financeiro'] as const;
type ViewMode = (typeof VIEW_MODES)[number];
const ABA: Record<ViewMode, { icone: typeof Ticket; rotulo: string }> = {
  eventos: { icone: Ticket, rotulo: 'Eventos' },
  painel: { icone: ChartBar, rotulo: 'Painel Executivo' },
  financeiro: { icone: ChartLineUp, rotulo: 'Financeiro' },
};
// Painel e Financeiro por ação do RBAC (tela "Eventos: abas e avisos", a mesma que o back cobra); o admin vê sempre.
const ACAO_DA_ABA: Partial<Record<ViewMode, string>> = {
  painel: 'ver_painel_executivo',
  financeiro: 'ver_financeiro',
};

export default function EventosPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // endereco antigo do evento (`/eventos?evento=ID`): hoje o evento tem pagina propria
  const eventoIdParam = searchParams.get('evento');
  const queryClient = useQueryClient();
  const { getEventos, createEvento, getAlertas } = useEventos();
  const { hasPermission } = useAuth();

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('eventos');
  const abas = VIEW_MODES.filter(m => {
    const acao = ACAO_DA_ABA[m];
    return !acao || hasPermission('/eventos-acoes', acao);
  });
  // aba sem permissão (tirada com a tela aberta) volta para a lista
  const aba = abas.includes(viewMode) ? viewMode : 'eventos';
  const [statusFiltro, setStatusFiltro] = useState<string>('ativo');
  const { isOpen: isFormOpen, onOpen: onFormOpen, onClose: onFormClose } = useDisclosure();
  const { isOpen: isConfigOpen, onOpen: onConfigOpen, onClose: onConfigClose } = useDisclosure();
  const abrirEvento = (id: number) => navigate(`/eventos/${id}`);

  // Debounce da busca (300ms)
  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data: eventosData, isLoading } = useQuery<IDataNoPagination<IEventoListItem[]>>({
    queryKey: ['eventos', { search: search || undefined, status: statusFiltro }],
    queryFn: getEventos,
  });

  const { data: alertasData } = useQuery<IDataNoPagination<IEventoAlerta[]>>({
    queryKey: ['eventos-alertas'],
    queryFn: getAlertas,
    staleTime: 30_000,
    refetchInterval: 5 * 60_000,
  });

  const createMutation = useMutation({
    mutationFn: createEvento,
    onSuccess: response => {
      queryClient.invalidateQueries({ queryKey: ['eventos'] });
      queryClient.invalidateQueries({ queryKey: ['kanban-projects'] });
      toast.success('Evento criado com sucesso!');
      onFormClose();
      if (response?.data?.id) {
        abrirEvento(response.data.id);
      }
    },
    onError: () => toast.error('Erro ao criar evento'),
  });

  const eventos = eventosData?.data ?? [];
  const alertas = alertasData?.data ?? [];

  const totalOrcamento = useMemo(
    () => eventos.reduce((s, e) => s + (e.valor_orcamento || 0), 0),
    [eventos],
  );

  const formatCompact = (val: number) => {
    if (val >= 1_000_000) return `R$ ${(val / 1_000_000).toFixed(1)}M`;
    if (val >= 1_000) return `R$ ${(val / 1_000).toFixed(0)}k`;
    return formatCurrency(val);
  };

  if (eventoIdParam) return <Navigate to={`/eventos/${eventoIdParam}`} replace />;

  return (
    <PageBox>
      <Box bg="gray.50" minH="100vh">
        {/* Header */}
        <Box bg="white" borderBottom="1px solid" borderColor="gray.100">
          <Box px={{ base: 6, md: 10 }} py={5}>
            <Flex justify="space-between" align="center">
              <Flex align="center" gap={3}>
                <Flex
                  w="40px"
                  h="40px"
                  borderRadius="12px"
                  bg="#EEF2FF"
                  align="center"
                  justify="center"
                >
                  <Ticket size={20} color="#6366F1" weight="bold" />
                </Flex>
                <Box>
                  <Text fontWeight="700" fontSize="lg" color="gray.800" letterSpacing="-0.02em">
                    Eventos
                  </Text>
                  <Text fontSize="12px" color="gray.400">
                    {eventos.length} evento{eventos.length !== 1 ? 's' : ''}{' '}
                    {totalOrcamento > 0 && `· ${formatCompact(totalOrcamento)} orçado`}
                  </Text>
                </Box>
              </Flex>
              <HStack spacing={3}>
                <AlertasDropdown alertas={alertas} onSelectEvento={abrirEvento} />
                <Tooltip label="Configuracoes">
                  <Button
                    variant="ghost"
                    size="sm"
                    borderRadius="full"
                    color="gray.400"
                    _hover={{ bg: 'gray.100', color: 'gray.600' }}
                    onClick={onConfigOpen}
                    p={2}
                  >
                    <GearSix size={18} />
                  </Button>
                </Tooltip>
                <Button
                  bg="gray.900"
                  color="white"
                  size="sm"
                  borderRadius="full"
                  fontWeight="500"
                  leftIcon={<Plus size={14} weight="bold" />}
                  onClick={onFormOpen}
                  _hover={{ bg: 'gray.800', transform: 'translateY(-1px)' }}
                  transition="all 0.15s ease"
                >
                  Novo Evento
                </Button>
              </HStack>
            </Flex>

            {/* Navigation Tabs */}
            <Flex align="center" justify="space-between" mt={4} flexWrap="wrap" gap={2}>
              <Flex align="center" gap={1}>
                <Tabs
                  variant="unstyled"
                  index={abas.indexOf(aba)}
                  onChange={i => setViewMode(abas[i])}
                >
                  <TabList gap={1}>
                    {abas.map(m => {
                      const { icone: Icone, rotulo } = ABA[m];
                      return (
                        <Tab
                          key={m}
                          fontSize="13px"
                          fontWeight="500"
                          px={4}
                          py={2}
                          borderRadius="full"
                          color="gray.500"
                          _selected={{ bg: 'gray.900', color: 'white' }}
                          transition="all 0.15s ease"
                        >
                          <Icone size={14} style={{ marginRight: 6 }} />
                          {rotulo}
                        </Tab>
                      );
                    })}
                  </TabList>
                </Tabs>
              </Flex>
            </Flex>
          </Box>
        </Box>

        {/* Content */}
        <Box px={{ base: 6, md: 10 }} py={6}>
          {aba === 'financeiro' && <FinanceiroDashboard />}
          {aba === 'painel' && <PainelExecutivo />}

          {aba === 'eventos' && (
            <Box>
              <HStack spacing={1} mb={3}>
                {[
                  { value: 'ativo', label: 'Ativos' },
                  { value: 'concluido', label: 'Finalizados' },
                  { value: 'cancelado', label: 'Arquivados' },
                ].map(opt => (
                  <Button
                    key={opt.value}
                    size="xs"
                    variant={statusFiltro === opt.value ? 'solid' : 'ghost'}
                    bg={statusFiltro === opt.value ? 'gray.900' : 'transparent'}
                    color={statusFiltro === opt.value ? 'white' : 'gray.500'}
                    borderRadius="full"
                    fontWeight="500"
                    fontSize="12px"
                    px={3}
                    _hover={{
                      bg: statusFiltro === opt.value ? 'gray.800' : 'gray.100',
                    }}
                    onClick={() => setStatusFiltro(opt.value)}
                  >
                    {opt.label}
                  </Button>
                ))}
              </HStack>

              <InputGroup mb={4} size="sm">
                <InputLeftElement pointerEvents="none">
                  <MagnifyingGlass size={15} color="#94A3B8" />
                </InputLeftElement>
                <Input
                  placeholder="Buscar evento..."
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  bg="white"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="10px"
                  _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px #3B82F6' }}
                />
              </InputGroup>

              {isLoading && (
                <Flex justify="center" py={16}>
                  <Spinner size="lg" color="gray.300" thickness="3px" />
                </Flex>
              )}

              {!isLoading && eventos.length === 0 && (
                <Flex
                  direction="column"
                  align="center"
                  py={16}
                  gap={4}
                  bg="white"
                  borderRadius="16px"
                  border="1px dashed"
                  borderColor="gray.200"
                >
                  <Flex
                    w="64px"
                    h="64px"
                    borderRadius="20px"
                    bg="gray.50"
                    align="center"
                    justify="center"
                  >
                    <Ticket size={28} color="#CBD5E0" weight="light" />
                  </Flex>
                  <Box textAlign="center">
                    <Text color="gray.500" fontWeight="500" fontSize="14px">
                      Nenhum evento
                    </Text>
                    <Text color="gray.400" fontSize="12px" mt={1}>
                      Crie seu primeiro evento para começar
                    </Text>
                  </Box>
                  <Button
                    size="sm"
                    bg="gray.900"
                    color="white"
                    borderRadius="full"
                    onClick={onFormOpen}
                    _hover={{ bg: 'gray.800' }}
                  >
                    <Plus size={14} style={{ marginRight: 6 }} />
                    Criar Evento
                  </Button>
                </Flex>
              )}

              <VStack spacing={3} align="stretch">
                {eventos.map((evento, idx) => {
                  const gradient = CARD_GRADIENTS[idx % CARD_GRADIENTS.length];
                  const statusCfg = evento.ultimo_status_contrato
                    ? STATUS_BADGE[evento.ultimo_status_contrato]
                    : null;

                  return (
                    <Box
                      key={evento.id}
                      as={RouterLink}
                      to={`/eventos/${evento.id}`}
                      display="block"
                      _focusVisible={{ boxShadow: 'outline', outline: 'none' }}
                      bg="white"
                      borderRadius="16px"
                      border="1px solid"
                      borderColor="gray.100"
                      overflow="hidden"
                      transition="all 0.2s ease"
                      _hover={{
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                        borderColor: 'gray.200',
                      }}
                      boxShadow="0 1px 2px rgba(0,0,0,0.04)"
                    >
                      <Box h="3px" bgGradient={gradient} />
                      <Box p={4}>
                        <Flex justify="space-between" align="start" mb={2}>
                          <Flex align="center" gap={2.5} flex={1} minW={0}>
                            <Flex
                              w="36px"
                              h="36px"
                              borderRadius="10px"
                              bg="gray.50"
                              align="center"
                              justify="center"
                              flexShrink={0}
                            >
                              <Text fontSize="13px" fontWeight="700" color="gray.500">
                                {evento.nome
                                  .split(/[\s—-]+/)
                                  .slice(0, 2)
                                  .map(w => w[0])
                                  .join('')
                                  .toUpperCase()}
                              </Text>
                            </Flex>
                            <Box minW={0} flex={1}>
                              <Text fontWeight="600" fontSize="14px" color="gray.800" noOfLines={1}>
                                {evento.nome}
                              </Text>
                              {evento.artista && (
                                <Text fontSize="12px" color="gray.400" noOfLines={1}>
                                  {evento.artista}
                                </Text>
                              )}
                              {evento.empresa_nome && (
                                <Text fontSize="11px" color="gray.300" noOfLines={1}>
                                  {evento.empresa_nome}
                                </Text>
                              )}
                            </Box>
                          </Flex>
                          <Text
                            fontSize="13px"
                            fontWeight="700"
                            color="gray.700"
                            flexShrink={0}
                            ml={2}
                          >
                            {formatCompact(evento.valor_orcamento)}
                          </Text>
                        </Flex>

                        <Flex justify="space-between" align="center" pl="46px">
                          <HStack spacing={1.5}>
                            {evento.dt_evento && (
                              <Badge
                                fontSize="10px"
                                fontWeight="500"
                                bg="gray.50"
                                color="gray.500"
                                px={2}
                                py={0.5}
                                borderRadius="md"
                                textTransform="none"
                              >
                                <CalendarBlank
                                  size={10}
                                  style={{ marginRight: 3, display: 'inline' }}
                                />
                                {moment(evento.dt_evento).format('DD MMM')}
                              </Badge>
                            )}
                            {evento.cidade && (
                              <Badge
                                fontSize="10px"
                                fontWeight="500"
                                bg="gray.50"
                                color="gray.500"
                                px={2}
                                py={0.5}
                                borderRadius="md"
                                textTransform="none"
                              >
                                <MapPin size={10} style={{ marginRight: 3, display: 'inline' }} />
                                {evento.cidade}
                              </Badge>
                            )}
                          </HStack>
                          <HStack spacing={1.5}>
                            {evento.total_alertas > 0 && (
                              <Tooltip label={`${evento.total_alertas} pendência(s)`}>
                                <Badge
                                  colorScheme="red"
                                  variant="solid"
                                  fontSize="10px"
                                  borderRadius="full"
                                  px={1.5}
                                >
                                  {evento.total_alertas}
                                </Badge>
                              </Tooltip>
                            )}
                            {statusCfg && (
                              <Badge
                                colorScheme={statusCfg.color}
                                fontSize="10px"
                                fontWeight="600"
                                borderRadius="md"
                                textTransform="none"
                              >
                                + {statusCfg.label}
                              </Badge>
                            )}
                          </HStack>
                        </Flex>
                      </Box>
                    </Box>
                  );
                })}
              </VStack>
            </Box>
          )}
        </Box>

        <EventoForm
          isOpen={isFormOpen}
          onClose={onFormClose}
          onSave={data => createMutation.mutate(data)}
          isLoading={createMutation.isPending}
        />

        <EventoConfigModal isOpen={isConfigOpen} onClose={onConfigClose} />
      </Box>
    </PageBox>
  );
}
